/**
 * Freigabeweg rechts neben den Einstellungen (Board „Preisautomatik · Einstellungen“): Entwurf →
 * Wirkung prüfen → Aktivieren → Aktiv. Jeder Schritt kommt aus echten Daten – der frischen Vorschau
 * (`calculatedAtUtc`) und der Speicherantwort dieser Sitzung –, nie aus einer Annahme über den
 * nächsten Lauf.
 */
import type { DynamicPricingMode } from '@/shared/api';

export type StepState = 'done' | 'now' | 'todo';

export interface ReleaseStep {
  label: string;
  detail: string;
  state: StepState;
}

/** Ergebnis des letzten Speicherns in dieser Sitzung. */
export interface SavedOutcome {
  versionNumber: number;
  previousVersionNumber: number;
  mode: DynamicPricingMode;
  previousMode: DynamicPricingMode;
  /** Uhrzeit der Vorschau, die zur Aktivierung geführt hat; bei „Aus“ keine. */
  previewedAt: string | null;
}

export interface ReleaseInput {
  mode: DynamicPricingMode;
  savedMode: DynamicPricingMode;
  dirty: boolean;
  changedSections: string[];
  versionNumber: number;
  /** „seit 12.10. 09:40“ – schon formatiert. */
  activeSince: string;
  /** Uhrzeit einer frischen Vorschau des aktuellen Entwurfs, sonst null. */
  previewedAt: string | null;
  saved: SavedOutcome | null;
}

const step = (label: string, detail: string, state: StepState): ReleaseStep => ({
  label,
  detail,
  state,
});

/** Satz nach dem Speichern – im Freigabeweg und als Rückmeldung. */
export function savedMessage(saved: SavedOutcome): string {
  if (saved.versionNumber === saved.previousVersionNumber) {
    return `Keine inhaltliche Änderung · Version ${saved.versionNumber} bleibt aktiv`;
  }
  if (saved.mode !== 'Off') {
    return `Version ${saved.versionNumber} ist aktiv · Neuberechnung ist angestoßen`;
  }
  return saved.previousMode === 'Off'
    ? `Version ${saved.versionNumber} ist gespeichert`
    : `Version ${saved.versionNumber} ist gespeichert · stoppt die Automatik, Werte bei Channex bleiben stehen`;
}

function activeDetail(saved: SavedOutcome): string {
  if (saved.versionNumber === saved.previousVersionNumber) return 'keine inhaltliche Änderung';
  if (saved.mode !== 'Off') return 'Neuberechnung ist angestoßen';
  return saved.previousMode === 'Off'
    ? 'Automatik bleibt aus'
    : 'Automatik gestoppt · Werte bei Channex bleiben stehen';
}

export function releaseSteps(input: ReleaseInput): ReleaseStep[] {
  const next = input.versionNumber + 1;
  if (!input.dirty && input.saved) {
    const off = input.saved.mode === 'Off';
    return [
      step('Entwurf', 'gespeichert', 'done'),
      off
        ? step('Wirkung prüfen', 'bei „Aus“ nicht nötig', 'done')
        : step('Wirkung geprüft', input.saved.previewedAt ?? 'geprüft', 'done'),
      step(off ? 'Gespeichert' : 'Aktiviert', `Version ${input.saved.versionNumber}`, 'done'),
      step('Aktiv', activeDetail(input.saved), 'done'),
    ];
  }
  if (!input.dirty) {
    return [
      step('Entwurf', 'Keine Änderungen', 'done'),
      step('Wirkung prüfen', 'nicht nötig', 'todo'),
      step('Aktivieren', '–', 'todo'),
      step('Aktiv', `Version ${input.versionNumber} ${input.activeSince}`, 'done'),
    ];
  }
  if (input.mode === 'Off') {
    return [
      step('Entwurf', 'Modus „Aus“', 'now'),
      step('Wirkung prüfen', 'bei „Aus“ nicht nötig', 'todo'),
      step(
        'Speichern',
        input.savedMode === 'Off' ? `als Version ${next}` : 'stoppt die Automatik sofort',
        'todo',
      ),
      step('Aktiv', `Version ${input.versionNumber} bis dahin`, 'todo'),
    ];
  }
  const changed = input.changedSections.length
    ? `Geändert: ${input.changedSections.join(', ')}`
    : 'Geändert';
  return [
    step('Entwurf', changed, 'now'),
    input.previewedAt
      ? step('Wirkung geprüft', input.previewedAt, 'done')
      : step('Wirkung prüfen', 'nötig vor dem Aktivieren', 'todo'),
    step(
      'Aktivieren',
      input.previewedAt ? `Version ${next} aktivieren` : 'nach der Prüfung',
      'todo',
    ),
    step('Aktiv', `Version ${input.versionNumber} bleibt bis dahin`, 'todo'),
  ];
}

/** Satz in der Aktionsleiste: was gerade gilt und was die Hauptaktion bewirkt. */
export function barText(input: ReleaseInput): { title: string; subline: string } {
  const next = input.versionNumber + 1;
  if (input.dirty) {
    const subline =
      input.mode !== 'Off'
        ? 'Vor dem Aktivieren rechnet der Server die Wirkung durch.'
        : input.savedMode === 'Off'
          ? `Speichern legt Version ${next} an.`
          : 'Speichern stoppt die Automatik für dieses Apartment.';
    return { title: 'Ungespeicherte Änderungen', subline };
  }
  return {
    title: input.saved ? savedMessage(input.saved) : 'Alles gespeichert',
    subline:
      input.savedMode === 'Off'
        ? `Version ${input.versionNumber} · Automatik aus`
        : `Version ${input.versionNumber} läuft.`,
  };
}
